<template>
  <div class="tenant-management p-4">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2>
        <i class="fas fa-building me-2"></i>
        Tenant Management
      </h2>
      <button class="btn btn-primary" @click="showCreateModal = true">
        <i class="fas fa-plus me-2"></i>
        Create Tenant
      </button>
    </div>

    <!-- Tenants List -->
    <div class="card">
      <div class="card-body">
        <div class="table-responsive">
          <table class="table table-striped table-hover">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Schema</th>
                <th>Subscription Tier</th>
                <th>Status</th>
                <th>Users / Clients</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="tenant in tenants" :key="tenant.tenantId">
                <td>
                  <div class="fw-bold">{{ tenant.tenantName }}</div>
                  <small class="text-muted">{{ tenant.tenantKey }}</small>
                </td>
                <td><code>{{ tenant.schemaName }}</code></td>
                <td>
                  <span class="badge bg-info">{{ tenant.subscriptionTierName }}</span>
                </td>
                <td>
                  <span class="badge" :class="tenant.isActive ? 'bg-success' : 'bg-secondary'">
                    {{ tenant.isActive ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td>
                  <small>{{ tenant.maxUsers }} / {{ tenant.maxClients }}</small>
                </td>
                <td>
                  <small>{{ formatDate(tenant.createdDate) }}</small>
                </td>
                <td>
                  <div class="btn-group btn-group-sm">
                    <button class="btn btn-outline-primary" @click="editTenant(tenant)" title="Edit">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button class="btn btn-outline-info" @click="viewUsage(tenant)" title="Usage">
                      <i class="fas fa-chart-bar"></i>
                    </button>
                    <button class="btn btn-outline-warning" @click="startImpersonation(tenant)" title="Impersonate">
                      <i class="fas fa-user-secret"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div class="modal" :class="{ 'show d-block': showCreateModal || showEditModal }" v-if="showCreateModal || showEditModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ showCreateModal ? 'Create Tenant' : 'Edit Tenant' }}</h5>
            <button type="button" class="btn-close" @click="closeModals"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveTenant">
              <div class="row">
                <div class="col-md-6 mb-3" v-if="showCreateModal">
                  <label class="form-label">Tenant Key *</label>
                  <input type="text" class="form-control" v-model="formData.tenantKey" required>
                  <small class="text-muted">URL-safe identifier (e.g., 'acme', 'widget-co')</small>
                </div>
                <div class="col-md-6 mb-3" v-if="showCreateModal">
                  <label class="form-label">Schema Name *</label>
                  <input type="text" class="form-control" v-model="formData.schemaName" required>
                  <small class="text-muted">SQL Server schema name</small>
                </div>
                <div class="col-md-12 mb-3">
                  <label class="form-label">Tenant Name *</label>
                  <input type="text" class="form-control" v-model="formData.tenantName" required>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Subscription Tier *</label>
                  <select class="form-select" v-model="formData.subscriptionTierId" required>
                    <option v-for="tier in subscriptionTiers" :key="tier.subscriptionTierId" :value="tier.subscriptionTierId">
                      {{ tier.tierName }} - ${{ tier.monthlyPrice }}/mo
                    </option>
                  </select>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Square Mode</label>
                  <select class="form-select" v-model="formData.squareMode">
                    <option value="sandbox">Sandbox</option>
                    <option value="live">Live</option>
                  </select>
                </div>
                <div class="col-md-12 mb-3" v-if="!showEditModal">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="formData.provisionSchema" id="provisionSchema">
                    <label class="form-check-label" for="provisionSchema">
                      Automatically provision schema and run migrations
                    </label>
                  </div>
                </div>
                <div class="col-md-12 mb-3" v-if="showEditModal">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" v-model="formData.isActive" id="isActive">
                    <label class="form-check-label" for="isActive">
                      Active
                    </label>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModals">Cancel</button>
            <button type="button" class="btn btn-primary" @click="saveTenant" :disabled="saving">
              <i class="fas fa-spinner fa-spin me-2" v-if="saving"></i>
              {{ showCreateModal ? 'Create' : 'Update' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showCreateModal || showEditModal"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import Api from '@/services/Api';

const tenants = ref<any[]>([]);
const subscriptionTiers = ref<any[]>([]);
const showCreateModal = ref(false);
const showEditModal = ref(false);
const saving = ref(false);

const formData = ref({
  tenantId: 0,
  tenantKey: '',
  tenantName: '',
  schemaName: '',
  subscriptionTierId: 0,
  squareMode: 'sandbox',
  provisionSchema: true,
  isActive: true
});

const loadTenants = async () => {
  try {
    const response = await Api.get('/api/admin/tenants');
    tenants.value = response.data;
  } catch (error: any) {
    console.error('Error loading tenants:', error);
    alert('Error loading tenants: ' + (error.response?.data?.error || error.message));
  }
};

const loadSubscriptionTiers = async () => {
  try {
    const response = await Api.get('/api/admin/tenants/subscription-tiers');
    subscriptionTiers.value = response.data;
  } catch (error: any) {
    console.error('Error loading subscription tiers:', error);
  }
};

const editTenant = (tenant: any) => {
  formData.value = {
    tenantId: tenant.tenantId,
    tenantKey: tenant.tenantKey,
    tenantName: tenant.tenantName,
    schemaName: tenant.schemaName,
    subscriptionTierId: tenant.subscriptionTierId,
    squareMode: tenant.squareMode || 'sandbox',
    provisionSchema: false,
    isActive: tenant.isActive
  };
  showEditModal.value = true;
};

const saveTenant = async () => {
  saving.value = true;
  try {
    if (showCreateModal.value) {
      await Api.post('/api/admin/tenants', formData.value);
      alert('Tenant created successfully!');
    } else {
      await Api.put(`/api/admin/tenants/${formData.value.tenantId}`, formData.value);
      alert('Tenant updated successfully!');
    }
    closeModals();
    await loadTenants();
  } catch (error: any) {
    console.error('Error saving tenant:', error);
    alert('Error saving tenant: ' + (error.response?.data?.message || error.message));
  } finally {
    saving.value = false;
  }
};

const viewUsage = async (tenant: any) => {
  try {
    const response = await Api.get(`/api/admin/tenants/${tenant.tenantId}/usage`);
    const usage = response.data;
    alert(`Usage for ${tenant.tenantName}:\n\nTechnicians: ${usage.currentTechnicians} / ${usage.maxTechnicians}\nClients: ${usage.currentClients} / ${usage.maxClients}\nMonthly Requests: ${usage.monthlyRequests}${usage.maxMonthlyRequests ? ' / ' + usage.maxMonthlyRequests : ' (unlimited)'}`);
  } catch (error: any) {
    console.error('Error loading usage:', error);
    alert('Error loading usage data');
  }
};

const startImpersonation = async (tenant: any) => {
  if (!confirm(`Start impersonating ${tenant.tenantName}? You will be logged into their tenant context.`)) {
    return;
  }
  
  try {
    const response = await Api.post('/api/admin/impersonation/start', { tenantId: tenant.tenantId });
    const { token } = response.data;
    
    // Store new token
    localStorage.setItem('token', token);
    
    alert(`Now impersonating ${tenant.tenantName}. Reload the page to see the tenant's data.`);
    window.location.reload();
  } catch (error: any) {
    console.error('Error starting impersonation:', error);
    alert('Error starting impersonation: ' + (error.response?.data?.message || error.message));
  }
};

const closeModals = () => {
  showCreateModal.value = false;
  showEditModal.value = false;
  formData.value = {
    tenantId: 0,
    tenantKey: '',
    tenantName: '',
    schemaName: '',
    subscriptionTierId: 0,
    squareMode: 'sandbox',
    provisionSchema: true,
    isActive: true
  };
};

const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString();
};

onMounted(() => {
  loadTenants();
  loadSubscriptionTiers();
});
</script>

<style scoped>
.tenant-management {
  max-width: 1400px;
  margin: 0 auto;
}

.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
